import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { actionCreators } from 'src/reducers/teacher';
import { actionCreators as clientProblemsActionCreators } from 'src/reducers/common/clientProblems';
import { Done } from '@mui/icons-material';
import SendIcon from '@mui/icons-material/Send';
import { green, red } from '@mui/material/colors';
import AvatarGroup from '@mui/material/AvatarGroup';
import { Avatar, Card, CardContent, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import translate from 'src/helpers/translate';
import IconButton from '@mui/material/IconButton';
import HomeworksTopBar from './HomeworksTopBar';
import homeworkRatingService from 'src/stateServices/homeworkRatingService';
import Delete from '@mui/icons-material/Delete';
import { colors } from '@mui/material';
import ConfirmationDialog from 'src/components/common/ConfirmationDialog';
import { useNavigate } from "react-router-dom";
import { getCookie, setCookie } from 'src/helpers/cookies';

function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
}

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: -35
    },
    small: {
        width: theme.spacing(4),
        height: theme.spacing(4),
        marginRight: '2px'
    },
    large: {
        width: theme.spacing(7),
        height: theme.spacing(7),
    },
    topActionBar: {
        marginTop: -55,
        marginBottom: 10
    },
    createNewButton: {
        marginRight: 5
    },
    filterByGroupButton: {
        marginBottom: 10,
    },
    cancelButton: {
        color: red[500],
        borderColor: red[500]
    },
    newRequestButton: {
        backgroundColor: green[200]
    },
    addNewButton: {
        marginRight: 10,
        marginBottom: 10,
        backgroundColor: green[500],
        '&:hover': {
            backgroundColor: green[700],
        }
    }, button: {
        marginLeft: '-10px',
        fontSize: 10
    },
    deleteButton: {
        marginLeft: '-10px',
        fontSize: 10,
        color: colors.red[400]
    },
    grid: {
        cursor: 'pointer'
    }
}));


export default function HomeworksGrid() {
    const dispatch = useDispatch();
    const classes = useStyles();
    let homeworks = useSelector(state => state.teacher.homeworks);
    const students = useSelector(state => state.teacher.students);
    const workState = useSelector(state => state.teacher.workState);
    const currentGroup = useSelector(state => state.teacher.groups.find(g => g.isCurrent));
    const currentStudent = useSelector(state => state.teacher.students.find(g => g.isCurrent));
    const [confirmationDialogMessage, setConfirmationDialogMessage] = useState('');
    const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState(false);
    const [homeworkIdToDelete, setHomeworkIdToDelete] = useState(0);
    const homeworksAreUpdating = useSelector(state => state.teacher.homeworksAreUpdating);
    let navigate = useNavigate();

    const shouldShowSwitch = workState.isGroup && currentGroup
        || workState.isStudent && currentStudent;

    const currentWorkEntity = workState.isGroup ? currentGroup?.name : currentStudent?.wholeName;

    let shouldShowForCurrentEntity = getCookie('showForCurrentEntity');

    shouldShowForCurrentEntity = shouldShowForCurrentEntity == undefined ? true : shouldShowForCurrentEntity == 'true';

    const [showForCurrentEntity, setShowForCurrentEntity] = useState(shouldShowForCurrentEntity);

    useEffect(() => {
        dispatch(actionCreators.requestHomeworks());
        dispatch(clientProblemsActionCreators.requestClientProblems());
    }, [dispatch])

    const problems = useSelector(state => state.common.problems);

    const clientProblems = useSelector(state => state.common.clientProblems);

    const topics = useSelector(state => state.common.topics);


    if (showForCurrentEntity) {
        if (workState.isGroup && currentGroup) {
            homeworks = homeworks.filter(h => h.groups.includes(currentGroup.id));
        } else if (workState.isStudent && currentStudent) {
            homeworks = homeworks.filter(h => h.students.includes(currentStudent.id));
        }
    }

    const homeworksToRender = homeworks.map(h => {
        return {
            ...h,
            students: students.filter(s => h.students.some(hs => hs === s.id)),
            joinedTopics: topics.filter(t => h.problems.map(hp => problems.find(p => p.id == hp)).some(p => p.endTopic == t.id)).map(t => translate(`TopicResources_${t.id}`)).filter(onlyUnique).join(', '),
            rating: homeworkRatingService.getRating(h, clientProblems, students)
        };
    }).sort((firstEl, secondEl) => secondEl.id - firstEl.id)

    const handleSend = (params, event) => {
        dispatch(actionCreators.sendHomework(params.id))
        event.stopPropagation();
    }

    const handleDelete = (params, event) => {
        setHomeworkIdToDelete(params.id);
        setConfirmationDialogMessage(translate('Delete').toLowerCase() + ' ' + params.row.description)
        setConfirmationDialogIsOpen(true);
        event.stopPropagation();
    }

    const columns = [
        { field: 'id', hide: true },
        {
            field: 'isSent',
            flex: 1,
            disableSelectionOnClick: true,
            headerName: translate('Status'),
            renderCell: (params) =>
                <React.Fragment>
                    <Tooltip title={translate('Delete')} arrow>
                        <IconButton
                            onClick={(event) => handleDelete(params, event)}
                            className={classes.deleteButton}
                            color="secondary"
                            aria-label="upload picture"
                            component="span"
                            size="large">
                            <Delete />
                        </IconButton>
                    </Tooltip>
                    {params.value &&
                        (
                            (<Tooltip title={translate('Sent')} arrow>
                                <Done />
                            </Tooltip>)
                        )
                    }
                    {!params.value &&
                        (<Tooltip title={translate(params.row.canBeSent ? 'Send' : 'HomeworkNeedsToBeCompletedBeforeSent')} arrow>
                            <span>
                                <IconButton
                                    onClick={(event) => handleSend(params, event)}
                                    className={classes.button}
                                    color="primary"
                                    aria-label="upload picture"
                                    component="span"
                                    disabled={!params.row.canBeSent}
                                    size="large">
                                    <SendIcon />
                                </IconButton>
                            </span>
                        </Tooltip>)
                    }
                </React.Fragment>
        },
        {
            field: 'description',
            flex: 2,
            headerName: translate('Description'),
            sortable: false
        },
        {
            field: 'createdOnString',
            flex: 1,
            headerName: translate('CreatedOn'),
            valueGetter: (params) => params.row.id,
            renderCell: (params) => <React.Fragment>{params.row.createdOnString}</React.Fragment>
        }
    ];

    if (homeworks.some(h => h.students !== null)) {
        columns.push({
            field: 'students',
            flex: 2,
            headerName: translate('Students'),
            renderCell: (params) => (
                <Tooltip title={params.value.map(s => s.wholeName).join(', ')} arrow>
                    <AvatarGroup max={5}>
                        {params.value.slice(0, 5).map(student => (
                            <Avatar
                                key={student?.id}
                                src={`data:image/jpg;base64, ${student?.avatarImage}`}
                                className={classes.small}
                            />
                        ))}
                    </AvatarGroup>
                </Tooltip>
            )
        })
    }

    if (homeworks.some(h => h.problems !== null)) {
        columns.push({
            field: 'joinedTopics',
            flex: 2,
            headerName: translate('Topics'),
            renderCell: (params) => (
                <Tooltip title={params.value} arrow>
                    <span>
                        {params.value.length < 20 ? params.value : params.value.substring(0, 20) + '...'}
                    </span>
                </Tooltip>
            )
        })
    }



    if (homeworks.some(h => h.endDate !== null)) {
        columns.push({
            field: 'endDateString',
            flex: 1,
            headerName: translate('DueDate'),
        })
    }

    columns.push({
        field: 'rating',
        flex: 1,
        headerName: translate('Rating'),
    })

    const [sortModel, setSortModel] = React.useState([
        {
            field: 'id',
            sort: 'desc',
        },
    ]);

    const handleRowClick = (params) => {
        navigate(`/app/homework/${params.id}`, { replace: true });
    };

    const onShowForCurrentEntityChange = (show) => {
        setShowForCurrentEntity(show);
        setCookie('showForCurrentEntity', show, 180);
    }

    return (
        <React.Fragment>
            <Card>
                <CardContent>
                    <HomeworksTopBar
                        shouldShowSwitch={shouldShowSwitch}
                        currentWorkEntity={currentWorkEntity}
                        showForCurrentEntity={showForCurrentEntity}
                        setShowForCurrentEntity={(show) => onShowForCurrentEntityChange(show)}
                    />
                    <DataGrid
                        loading={homeworksAreUpdating}
                        rows={homeworksToRender}
                        density="compact"
                        columns={columns}
                        pageSize={15}
                        autoHeight={true}
                        disableColumnMenu
                        className={classes.grid}
                        onRowClick={handleRowClick}
                        hideFooter={homeworksToRender.length <= 15}
                        hideFooterSelectedRowCount />
                </CardContent>
            </Card>
            <ConfirmationDialog
                open={confirmationDialogIsOpen}
                onCancel={() => setConfirmationDialogIsOpen(false)}
                onSubmit={() => dispatch(actionCreators.deleteHomework(homeworkIdToDelete, navigate))}
                message={confirmationDialogMessage}
            />
        </React.Fragment>

    );
}